// src/components/ServiceRecovery/serviceRecoveryApi.js
// Backend requirement: custom:tenant_id must be in the JWT. Use the Cognito ID token
// (session.tokens.idToken), not the access token—custom claims exist only in the ID token.
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

class ServiceRecoveryApiService {
  /**
   * Auth headers: Cognito ID token only (not access token).
   * Backend expects custom:tenant_id in the JWT; that claim is only in the ID token.
   */
  async getAuthHeaders() {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        throw new Error('ID token not available');
      }
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw new Error('Authentication failed');
    }
  }

  /**
   * Get tenant ID from the Cognito ID token (custom:tenant_id).
   * Must use ID token payload, not access token—custom claims are only in the ID token.
   * Falls back to /tenant API, then to default (env or "0") so assign/recovery work when
   * custom:tenant_id is not yet set in Cognito.
   */
  async getTenantId() {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    const payload = idToken?.payload;
    if (payload) {
      const fromToken = payload['custom:tenant_id'] || payload.tenant_id;
      if (fromToken) return String(fromToken);
    }
    try {
      const data = await this.makeRequest('/tenant');
      const fromApi = data?.data?.tenant_id ?? data?.tenant_id ?? data?.id;
      if (fromApi != null) return String(fromApi);
    } catch (_) {
      // /tenant failed or not available
    }
    const defaultTenant =
      process.env.REACT_APP_DEFAULT_TENANT_ID != null
        ? process.env.REACT_APP_DEFAULT_TENANT_ID
        : '0';
    console.warn(
      '[ServiceRecovery] Tenant ID not in ID token (custom:tenant_id). Using default:',
      defaultTenant,
      '— Ask your administrator to set custom:tenant_id in Cognito for your user, then sign out and sign in again.'
    );
    return defaultTenant;
  }

  async makeRequest(endpoint, options = {}) {
    const headers = await this.getAuthHeaders();
    const url = `${API_BASE_URL}${endpoint}`;

    const config = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      // Handle CORS errors
      if (!response.ok && response.status === 0) {
        throw new Error(
          `CORS Error: The server at ${API_BASE_URL} is not allowing requests from this origin. Please check server CORS configuration.`
        );
      }

      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // If response is not JSON, try to get text
        const text = await response.text();
        throw new Error(
          `Unexpected response format from ${endpoint}. Expected JSON but received: ${contentType || 'unknown'}`
        );
      }

      if (!response.ok) {
        const message =
          (data && (data.error || data.message)) ||
          `HTTP ${response.status}: ${response.statusText}`;
        const err = new Error(message);
        err.status = response.status;
        err.endpoint = endpoint;
        err.responseData = data;
        
        // Log API error to logging service (skip 404 as they may be expected)
        if (err.status !== 404) {
          loggingService.logError(
            'api_error_service_recovery',
            `Service Recovery API error: ${response.status} ${response.statusText} for ${endpoint}`,
            err,
            {
              endpoint,
              method: options.method || 'GET',
              status: response.status,
              statusText: response.statusText,
              responseData: data,
            }
          ).catch(logError => {
            console.warn('[serviceRecoveryApi] Failed to log error:', logError);
          });
        }
        
        throw err;
      }

      // Log API event for successful request
      loggingService.logInfo(
        'api_success_service_recovery',
        `Successfully completed ${options.method || 'GET'} request to ${endpoint}`,
        {
          endpoint,
          method: options.method || 'GET',
        }
      ).catch(logError => {
        console.warn('[serviceRecoveryApi] Failed to log success event:', logError);
      });

      return data;
    } catch (error) {
      // Enhanced error handling for CORS and network errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        const corsError = new Error(
          `Network/CORS Error: Unable to connect to ${API_BASE_URL}${endpoint}. ` +
          `This may be due to: 1) CORS policy blocking the request, 2) Network connectivity issues, ` +
          `3) Server is down, or 4) Endpoint does not exist. Please check server configuration.`
        );
        corsError.name = 'CORS_ERROR';
        corsError.endpoint = endpoint;
        console.error(`❌ CORS/Network error for ${endpoint}:`, corsError.message);
        
        // Log CORS/network errors to logging service
        loggingService.logError(
          'api_error_service_recovery',
          `Service Recovery API CORS/network error for ${endpoint}`,
          corsError,
          {
            endpoint,
            method: options.method || 'GET',
            errorType: 'CORS_ERROR',
          }
        ).catch(logError => {
          console.warn('[serviceRecoveryApi] Failed to log error:', logError);
        });
        
        throw corsError;
      }
      // Don't log 404 as error - caller may treat it as "not found" (e.g. guest profile)
      if (error.status !== 404) {
        console.error(`❌ API request failed for ${endpoint}:`, error);
        
        // Log network/fetch errors to logging service (skip 404)
        loggingService.logError(
          'api_error_service_recovery',
          `Service Recovery API network/fetch error for ${endpoint}`,
          error,
          {
            endpoint,
            method: options.method || 'GET',
            errorType: error.name || 'NetworkError',
          }
        ).catch(logError => {
          console.warn('[serviceRecoveryApi] Failed to log error:', logError);
        });
      }
      throw error;
    }
  }

  // Submit guest feedback (from QR/bill)
  // Lambda: feedback submission Lambda → Amazon Comprehend sentiment → store feedback
  async submitFeedback(feedbackData) {
    const { order_id, rating, feedback_category, feedback_text, request_callback } = feedbackData;

    return this.makeRequest('/guest-feedback', {
      method: 'POST',
      body: JSON.stringify({
        order_id,
        rating, // 1-5 stars or thumbs up/down
        feedback_category, // Food/Service/Cleanliness
        feedback_text, // Optional text feedback
        request_callback, // Boolean
      }),
    });
  }

  // Get manager alerts (from alert table)
  // EventBridge rule triggers manager alert when sentiment < threshold
  async getManagerAlerts(limit = 50, exclusiveStartKey = null) {
    const params = new URLSearchParams({ 
      limit: limit.toString(),
      alert_type: 'guest_feedback'
    });
    if (exclusiveStartKey) {
      params.append('exclusiveStartKey', JSON.stringify(exclusiveStartKey));
    }
    return this.makeRequest(`/alerts?${params}`);
  }

  // Get feedback by order ID
  async getFeedbackByOrderId(orderId) {
    return this.makeRequest(`/guest-feedback/order/${orderId}`);
  }

  // Get guest feedback history (for CRM enrichment)
  async getGuestFeedbackHistory(orderId, guestId = null) {
    const params = new URLSearchParams({ order_id: orderId });
    if (guestId) {
      params.append('guest_id', guestId);
    }
    return this.makeRequest(`/guest-feedback/history?${params}`);
  }

  /**
   * Get dish dataset: order items with dish info, table, server, status, etc.
   * GET /restaurant/dish-dataset?site_id=...&start_date=...&end_date=...
   * @param {string} siteId - Site ID to filter by
   * @param {string} startDate - Start date/time (ISO string), e.g. today 00:00:00
   * @param {string} endDate - End date/time (ISO string), e.g. now
   * @returns {Promise<Array>} Array of order item records e.g. [{ order_event_timestamp, table_no, order_ended, server_user_id, site_id, order_item_id, dish_id, notes, qty, current_status, cust_mods, stages, dish_info: { name, cuisine, preference, Price, type } }, ...]
   */
  async getDishDataset(siteId, startDate, endDate) {
    const params = new URLSearchParams();
    if (siteId) params.set('site_id', siteId);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    const query = params.toString();
    return this.makeRequest(`/restaurant/dish-dataset${query ? `?${query}` : ''}`);
  }

  /**
   * Record recovery action for a feedback item.
   * POST /guest-feedback/recovery/{feedbackId}
   * Backend may require tenant_id (query, header, body) for authorization.
   */
  async logRecoveryAction(feedbackId, recoveryData) {
    const tenantIdRaw = await this.getTenantId();
    const tenantId = tenantIdRaw != null ? String(tenantIdRaw) : null;
    if (!tenantId) {
      throw new Error(
        'Tenant ID missing. Your account may not have tenant context. Please sign out and sign in again, or contact your administrator.'
      );
    }

    const recovery_action_taken =
      recoveryData.recovery_action_taken ?? recoveryData.action_description ?? '';
    const guest_sentiment_change_index =
      recoveryData.guest_sentiment_change_index ?? recoveryData.guest_sentiment_change ?? 0;
    const recovery_notes = recoveryData.recovery_notes ?? '';

    const query = new URLSearchParams({ tenant_id: tenantId }).toString();
    return this.makeRequest(`/guest-feedback/recovery/${feedbackId}?${query}`, {
      method: 'POST',
      body: JSON.stringify({
        recovery_action_taken,
        guest_sentiment_change_index: Number(guest_sentiment_change_index),
        recovery_notes,
        tenant_id: tenantId,
      }),
    });
  }

  /**
   * Assign alert to staff member.
   * PUT /alerts/{alertId}/assign
   * Backend may require tenant_id in JWT, query, header, and/or body.
   */
  async assignAlertToStaff(alertId, staffId) {
    const tenantIdRaw = await this.getTenantId();
    const tenantId = tenantIdRaw != null ? String(tenantIdRaw) : null;
    if (!tenantId) {
      throw new Error(
        'Tenant ID missing. Your account may not have tenant context. Please sign out and sign in again, or contact your administrator.'
      );
    }
    const query = new URLSearchParams({ tenant_id: tenantId }).toString();
    return this.makeRequest(`/alerts/${alertId}/assign?${query}`, {
      method: 'PUT',
      body: JSON.stringify({
        assigned_to: staffId,
        tenant_id: tenantId,
      }),
    });
  }

  /**
   * Get feedback analytics.
   * GET /guest-feedback/analytics?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD&site_id= (optional)
   * Both start_date and end_date required for date filtering.
   */
  async getFeedbackAnalytics(dateRange = { startDate: null, endDate: null }, siteId = null) {
    const params = new URLSearchParams();
    const startDate = dateRange.startDate
      ? this.formatDateForApi(new Date(dateRange.startDate)) || dateRange.startDate
      : null;
    const endDate = dateRange.endDate
      ? this.formatDateForApi(new Date(dateRange.endDate)) || dateRange.endDate
      : null;
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (siteId) params.append('site_id', siteId);
    const query = params.toString();
    return this.makeRequest(`/guest-feedback/analytics${query ? `?${query}` : ''}`);
  }

  /**
   * Get recovery analytics.
   * GET /guest-feedback/recovery-analytics?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD&site_id= (optional)
   * Both start_date and end_date required for date filtering.
   */
  async getRecoveryActionsAnalytics(dateRange = { startDate: null, endDate: null }, siteId = null) {
    const params = new URLSearchParams();
    const startDate = dateRange.startDate
      ? this.formatDateForApi(new Date(dateRange.startDate)) || dateRange.startDate
      : null;
    const endDate = dateRange.endDate
      ? this.formatDateForApi(new Date(dateRange.endDate)) || dateRange.endDate
      : null;
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (siteId) params.append('site_id', siteId);
    const query = params.toString();
    const endpoint = `/guest-feedback/recovery-analytics${query ? `?${query}` : ''}`;
    return this.makeRequest(endpoint);
  }

  // Get guest profile/history (CRM enrichment)
  // Returns null when profile is not found (404); throws for other errors.
  async getGuestProfile(orderOrFeedbackId) {
    try {
      const response = await this.makeRequest(`/guest-feedback/guest-profile/${orderOrFeedbackId}`);
      return response;
    } catch (error) {
      // 404 = no guest profile for this order/feedback - treat as empty, not an error
      if (error.status === 404) {
        return null;
      }
      console.error('❌ serviceRecoveryApi.getGuestProfile: Error:', error);
      throw error;
    }
  }

  // Helper method to format dates for API
  formatDateForApi(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Helper method to get sentiment severity
  getSentimentSeverity(sentimentScore) {
    if (sentimentScore >= 0.7) {
      return { level: 'positive', color: '#10B981', label: 'Positive' };
    } else if (sentimentScore >= 0.4) {
      return { level: 'neutral', color: '#F59E0B', label: 'Neutral' };
    } else {
      return { level: 'negative', color: '#EF4444', label: 'Negative' };
    }
  }

  // Helper method to get feedback categories
  getFeedbackCategories() {
    return [
      { value: 'Food', label: 'Food', icon: '🍽️' },
      { value: 'Service', label: 'Service', icon: '👥' },
      { value: 'Cleanliness', label: 'Cleanliness', icon: '🧹' },
    ];
  }

  // ==================== New Feedback API Endpoints ====================

  /**
   * Create Feedback
   * POST /feedback
   * @param {Object} feedbackData - Feedback data
   * @param {string} feedbackData.site_id - Site ID (required)
   * @param {string} feedbackData.order_id - Order ID (required)
   * @param {string} feedbackData.feedback_text - Feedback text
   * @param {string} feedbackData.feedback_category - Category: "food|service|ambiance|pricing|other"
   * @param {number} feedbackData.sentiment_score - Sentiment score (-1 to 1, optional - auto-calculated if feedback_text provided)
   * @param {string} feedbackData.recovery_action_taken - Recovery action description
   * @param {number} feedbackData.guest_sentiment_change_index - Sentiment change after recovery (0 to 1)
   * @returns {Promise<Object>} Response object with feedback data
   */
  async createFeedback(feedbackData) {
      console.log('📡 serviceRecoveryApi.createFeedback: Calling API with data:', feedbackData);
    try {
      const response = await this.makeRequest('/feedback', {
        method: 'POST',
        body: JSON.stringify(feedbackData),
      });
      console.log('📡 serviceRecoveryApi.createFeedback: API Response:', response);
      
      // Log API event for creating feedback (additional to makeRequest logging)
      loggingService.logInfo(
        'api_success_service_recovery',
        `Successfully created feedback`,
        {
          endpoint: '/feedback',
          method: 'POST',
          siteId: feedbackData.site_id,
          orderId: feedbackData.order_id,
          feedbackCategory: feedbackData.feedback_category,
        }
      ).catch(logError => {
        console.warn('[serviceRecoveryApi] Failed to log success event:', logError);
      });
      
      return response;
    } catch (error) {
      console.error('❌ serviceRecoveryApi.createFeedback: Error:', error);
      throw error;
    }
  }

  /**
   * List Feedback
   * GET /feedback?site_id={site_id}&from={ISO8601}&to={ISO8601}
   * @param {Object} filters - Filter parameters
   * @param {string} filters.site_id - Site ID (optional)
   * @param {string} filters.from - Start date in ISO8601 format (optional)
   * @param {string} filters.to - End date in ISO8601 format (optional)
   * @returns {Promise<Object>} Response object with feedback list
   */
  async listFeedback(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);

      const endpoint = params.toString() ? `/feedback?${params.toString()}` : '/feedback';
      console.log('📡 serviceRecoveryApi.listFeedback: Calling API endpoint:', endpoint);
      
      const response = await this.makeRequest(endpoint, {
        method: 'GET',
      });
      console.log('📡 serviceRecoveryApi.listFeedback: API Response:', response);
      
      // Log API event for listing feedback (additional to makeRequest logging)
      loggingService.logInfo(
        'api_success_service_recovery',
        `Successfully listed feedback`,
        {
          endpoint: '/feedback',
          method: 'GET',
          ...filters,
        }
      ).catch(logError => {
        console.warn('[serviceRecoveryApi] Failed to log success event:', logError);
      });
      
      return response;
    } catch (error) {
      console.error('❌ serviceRecoveryApi.listFeedback: Error:', error);
      throw error;
    }
  }

  /**
   * Get Feedback
   * GET /feedback/{feedback_id}
   * @param {string} feedbackId - Feedback ID
   * @returns {Promise<Object>} Response object with feedback data
   */
  async getFeedback(feedbackId) {
    try {
      console.log('📡 serviceRecoveryApi.getFeedback: Calling API for feedback ID:', feedbackId);
      const response = await this.makeRequest(`/feedback/${feedbackId}`, {
        method: 'GET',
      });
      console.log('📡 serviceRecoveryApi.getFeedback: API Response:', response);
      
      // Log API event for getting feedback (additional to makeRequest logging)
      loggingService.logInfo(
        'api_success_service_recovery',
        `Successfully fetched feedback ${feedbackId}`,
        {
          endpoint: `/feedback/${feedbackId}`,
          method: 'GET',
          feedbackId,
        }
      ).catch(logError => {
        console.warn('[serviceRecoveryApi] Failed to log success event:', logError);
      });
      
      return response;
    } catch (error) {
      console.error('❌ serviceRecoveryApi.getFeedback: Error:', error);
      throw error;
    }
  }

  /**
   * Update Feedback
   * PUT /feedback/{feedback_id}
   * @param {string} feedbackId - Feedback ID
   * @param {Object} feedbackData - Updated feedback data
   * @returns {Promise<Object>} Response object with updated feedback data
   */
  async updateFeedback(feedbackId, feedbackData) {
    try {
      console.log('📡 serviceRecoveryApi.updateFeedback: Calling API for feedback ID:', feedbackId);
      console.log('📡 serviceRecoveryApi.updateFeedback: Update data:', feedbackData);
      const response = await this.makeRequest(`/feedback/${feedbackId}`, {
        method: 'PUT',
        body: JSON.stringify(feedbackData),
      });
      console.log('📡 serviceRecoveryApi.updateFeedback: API Response:', response);
      
      // Log API event for updating feedback (additional to makeRequest logging)
      loggingService.logInfo(
        'api_success_service_recovery',
        `Successfully updated feedback ${feedbackId}`,
        {
          endpoint: `/feedback/${feedbackId}`,
          method: 'PUT',
          feedbackId,
          updatedFields: Object.keys(feedbackData || {}),
        }
      ).catch(logError => {
        console.warn('[serviceRecoveryApi] Failed to log success event:', logError);
      });
      
      return response;
    } catch (error) {
      console.error('❌ serviceRecoveryApi.updateFeedback: Error:', error);
      throw error;
    }
  }

  /**
   * Helper method to get new feedback categories (matching API spec)
   */
  getNewFeedbackCategories() {
    return [
      { value: 'food', label: 'Food', icon: '🍽️' },
      { value: 'service', label: 'Service', icon: '👥' },
      { value: 'ambiance', label: 'Ambiance', icon: '🏛️' },
      { value: 'pricing', label: 'Pricing', icon: '💰' },
      { value: 'other', label: 'Other', icon: '📝' },
    ];
  }
}

export default new ServiceRecoveryApiService();
